import React, { useState } from 'react';
import { requestForgotPasswordOTPAPI, verifyForgotPasswordOTPAPI, resetPasswordWithOTPAPI } from '../../API/Login/ForgotPasswordAPI';
import { errorSnackMsg } from '../Custom Hooks/SnackkBarUtilities';

const ForgotPassword = ({ userName, companyCode, onClose, onSuccess }) => {
  const [step, setStep] = useState(1); // 1: Request OTP, 2: Verify OTP, 3: Reset Password
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [loading, setLoading] = useState(false);

  // Step 1: Request OTP
  const handleRequestOTP = async () => {
    if (!userName || !companyCode) {
      errorSnackMsg("Please enter username and company code first");
      return;
    }

    setLoading(true);
    try {
      const response = await requestForgotPasswordOTPAPI({
        userName,
        companyCode,
        prefix: 'RST',
      });
      
      setMaskedEmail(response.data.email);
      setStep(2);
    } catch (err) {
      console.error('Request OTP error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      errorSnackMsg("Please enter a valid 6-digit OTP");
      return;
    }

    setLoading(true);
    try {
      await verifyForgotPasswordOTPAPI({
        userName,
        companyCode,
        otp,
        prefix: 'RST',
      });
      
      setStep(3);
    } catch (err) {
      console.error('Verify OTP error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Reset Password
  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      errorSnackMsg("Password must be at least 6 characters");
      return;
    }

    if (newPassword !== confirmPassword) {
      errorSnackMsg("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      await resetPasswordWithOTPAPI({
        userName,
        companyCode,
        otp,
        newPassword,
        prefix: 'RST',
      });
      
      // Success - close dialog and notify parent
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      console.error('Reset password error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '12px',
        maxWidth: '450px',
        width: '100%',
        boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
      }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h2 style={{ margin: 0, color: '#333', fontSize: '24px' }}>
            🔐 Forgot Password
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#999',
              padding: '0',
              width: '30px',
              height: '30px'
            }}
          >
            ×
          </button>
        </div>

        {/* Step Indicator */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '30px',
          position: 'relative'
        }}>
          {[1, 2, 3].map((s) => (
            <div key={s} style={{ 
              flex: 1, 
              textAlign: 'center',
              position: 'relative',
              zIndex: 1
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: step >= s ? '#4CAF50' : '#e0e0e0',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 8px',
                fontWeight: 'bold',
                fontSize: '16px'
              }}>
                {s}
              </div>
              <div style={{ 
                fontSize: '11px', 
                color: step >= s ? '#4CAF50' : '#999',
                fontWeight: step === s ? 'bold' : 'normal'
              }}>
                {s === 1 ? 'Request' : s === 2 ? 'Verify' : 'Reset'}
              </div>
            </div>
          ))}
          <div style={{
            position: 'absolute',
            top: '20px',
            left: '20%',
            right: '20%',
            height: '2px',
            backgroundColor: '#e0e0e0',
            zIndex: 0
          }}>
            <div style={{
              height: '100%',
              backgroundColor: '#4CAF50',
              width: step === 1 ? '0%' : step === 2 ? '50%' : '100%',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>

        {/* Step 1: Request OTP */}
        {step === 1 && (
          <div>
            <p style={{ color: '#666', marginBottom: '20px', lineHeight: '1.6' }}>
              We'll send a verification code to your registered email address to help you reset your password.
            </p>
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '15px',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Username</div>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#333' }}>{userName}</div>
            </div>
            <button
              onClick={handleRequestOTP}
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px',
                backgroundColor: loading ? '#ccc' : '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: 'bold',
                transition: 'background-color 0.3s'
              }}
            >
              {loading ? 'Sending...' : 'Send OTP'}
            </button>
          </div>
        )}

        {/* Step 2: Verify OTP */}
        {step === 2 && (
          <div>
            <p style={{ color: '#666', marginBottom: '15px', lineHeight: '1.6' }}>
              Enter the 6-digit code sent to <strong>{maskedEmail}</strong>
            </p>
            <input
              type="text"
              placeholder="Enter 6-digit OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              style={{
                width: '100%',
                padding: '14px',
                fontSize: '20px',
                border: '2px solid #ddd',
                borderRadius: '8px',
                marginBottom: '20px',
                textAlign: 'center',
                letterSpacing: '8px',
                fontWeight: 'bold'
              }}
              maxLength={6}
              autoFocus
            />
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setStep(1)}
                style={{
                  flex: 1,
                  padding: '14px',
                  backgroundColor: '#f0f0f0',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}
              >
                Back
              </button>
              <button
                onClick={handleVerifyOTP}
                disabled={loading || otp.length !== 6}
                style={{
                  flex: 2,
                  padding: '14px',
                  backgroundColor: (loading || otp.length !== 6) ? '#ccc' : '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (loading || otp.length !== 6) ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}
              >
                {loading ? 'Verifying...' : 'Verify OTP'}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Reset Password */}
        {step === 3 && (
          <div>
            <p style={{ color: '#666', marginBottom: '20px', lineHeight: '1.6' }}>
              Create a new password for your account
            </p>
            <input
              type="password"
              placeholder="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              style={{
                width: '100%',
                padding: '14px',
                fontSize: '16px',
                border: '2px solid #ddd',
                borderRadius: '8px',
                marginBottom: '15px'
              }}
              autoFocus
            />
            <input
              type="password"
              placeholder="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={{
                width: '100%',
                padding: '14px',
                fontSize: '16px',
                border: '2px solid #ddd',
                borderRadius: '8px',
                marginBottom: '20px'
              }}
            />
            <button
              onClick={handleResetPassword}
              disabled={loading || !newPassword || !confirmPassword}
              style={{
                width: '100%',
                padding: '14px',
                backgroundColor: (loading || !newPassword || !confirmPassword) ? '#ccc' : '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: (loading || !newPassword || !confirmPassword) ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
